import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling the scroll-based opacity transition of the featured content's circle
 */
export default class FeaturedContent extends MessageBus {
  /**
   * Constructor function of the FeaturedContent class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();

    /**
     * DOM node that is passed into the constructor
     * @property {HTMLElement} element - The module's container
     */
    this.element = element;

    /**
     * Cached DOM references for key elements
     * @property {HTMLElement} circleInner - The inner circle element
     * @property {HTMLElement} circleWrapper - The outer wrapper for the circle
     */
    this.circleInner = null;
    this.circleWrapper = null;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by caching DOM references, setting up handlers, and enabling event listeners.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Caches DOM references for elements required in the scroll logic.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.circleInner = this.element.querySelector(
      `.${CLASS_NAMES.PRIMARY_FEATURED_CONTENT_CIRCLE_INNER}`
    );
    this.circleWrapper = this.element.querySelector(
      `.${CLASS_NAMES.PRIMARY_FEATURED_CONTENT_CIRCLE}`
    );

    return this;
  }

  /**
   * Enables event listeners for the module.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (!this.circleInner || !this.circleWrapper) return this;

    window.addEventListener(EVENTS.SCROLL, this.handleScroll);
    return this;
  }

  /**
   * Binds event handlers with the proper context of `this`.
   * @returns {FeaturedContent} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleScroll = this.onScrollEvent.bind(this);
    return this;
  }

  /**
   * Handles the scroll event, managing the fade effect for the inner circle.
   */
  onScrollEvent() {
    if (!this.circleInner || !this.circleWrapper) return;

    const wrapperTop = this.circleWrapper.getBoundingClientRect().top;
    const isSticky = wrapperTop <= 0;

    if (isSticky) {
      const delayBeforeFade = 300;
      const fadeStart = delayBeforeFade;
      const fadeEnd = fadeStart + 400;

      const scrolledPastSticky = Math.abs(wrapperTop);

      if (scrolledPastSticky <= fadeStart) {
        this.circleInner.style.opacity = 1;
      } else {
        const opacity =
          1 - (scrolledPastSticky - fadeStart) / (fadeEnd - fadeStart);
        this.circleInner.style.opacity = Math.max(0, Math.min(1, opacity));
      }
    } else {
      this.circleInner.style.opacity = 1;
    }
  }
}
